/**
 * THE PROVIDER SEAM — what a signal family contributes to the Interactive Trade Simulator.
 *
 * <h2>One adapter per family, and CTS is the only one implemented</h2>
 *
 * <pre>
 *   label      what the card calls it
 *   rulesFor   the execution rules: target, stop, horizon
 *   metadata   the rows on the signal card
 * </pre>
 *
 * PTA and PTB are NOT stubbed here. A stub returning nothing is dead code that reads as a
 * feature, and the two will not share CTS's rules: they carry zones and band-height geometry and
 * their target and stop will be structural levels rather than multiples of volatility. What this
 * file provides is the shape they plug into — `simulateTrade` already takes rules rather than
 * computing them, and `tradeSimSession` already keys a trade by `{provider, date}`, so adding
 * PTA is an entry in this table and a geometric `rulesFor`.
 *
 * <h2>An unknown provider is REFUSED, not defaulted</h2>
 *
 * Falling back to CTS would run a volatility rule under a geometric signal's name and report it
 * as that signal's result. A null is a card that says it cannot evaluate this, which is visible.
 */

/**
 * HOW A FAMILY IS EXECUTED, and this is the real axis the two kinds of card differ on.
 *
 * <pre>
 *   AUTO     a click IS the trade. The engine prices it from the family's own rules.
 *   MANUAL   a click SELECTS. The reader locks a direction and clicks Open and Close.  PTA, PTB
 *   BOTH     a click does both: the engine's assessment AND an armed manual execution.  CTS
 * </pre>
 *
 * <p>Declared on the adapter rather than inferred from "does this family have rules", because
 * the absence of a rule set and a deliberate hand-off to the operator are different facts and
 * only one of them is a feature.
 *
 * <p>A THIRD VALUE rather than two orthogonal booleans. `assesses` and `executable` would be
 * four combinations of which one — neither — is meaningless, and a family that is neither is a
 * family with no reason to be in the table. Three named states, two derived predicates.
 *
 * <p>AUTO currently names no family: CTS moved to BOTH when manual execution reached it. It is
 * kept because it is a coherent configuration — a family whose rules price a trade and which
 * hands the reader nothing — and removing it would make `assessesAutomatically` look like it
 * means "is CTS".
 */
/**
 * Does a click on this family price a theoretical trade from its own rules?
 *
 * <p>ONE BOOLEAN, where there used to be a three-valued `MODES` enum and three predicates
 * derived from it. The other two asked whether a family allowed manual execution and whether
 * it allowed ONLY that — questions with one answer each now, because the execution strip is
 * permanent and needs no provider at all. A reader can take a trade on a bare chart.
 *
 * <p>What survives is the distinction that still does work: CTS prices a trade the moment its
 * marker is clicked, and a channel does not.
 */
export const assessesAutomatically = (key) => {
  const p = PROVIDERS[String(key || '').toUpperCase()];
  return !!p && p.assesses === true;
};

/** The CTS execution rule, and every number in it is a decision. */
export const CTS_RULES = Object.freeze({
  /**
   * TEN BARS — the same window `ctsOddsEngine` measures its odds over.
   *
   * <p>Deliberately the same, and this is the load-bearing choice of the whole feature. The card
   * shows an odds figure and a result side by side; measured over different windows they would
   * be answering different questions, and a reader would hold one against the other as if they
   * were the same question asked twice. See `DEFAULT_HORIZON` in the odds engine.
   */
  horizon: 10,
  /**
   * ONE ATR EACH WAY, which makes the rule direction-neutral and R meaningful.
   *
   * <p>Symmetric on purpose. An asymmetric pair would embed a view about CTS's payoff profile
   * into the measuring instrument, so a favourable result could come from the rule rather than
   * from the signal — and this dialog exists to let a reader judge the signal.
   *
   * <p>In ATRs rather than percent or points: a fixed percentage is most of a quiet 1990s
   * session's whole range and a rounding error against an 8% day in March 2020, and a fixed
   * point offset is not scale-free at all across a 15x price range. The same argument that put
   * the structural tolerance in ATRs.
   */
  targetAtr: 1.0,
  stopAtr: 1.0,
});

/**
 * CTS CARRIES NO CHANNEL GEOMETRY AT ALL — not in its rules, and not on its card.
 *
 * <h2>What the card shows, and the one thing it deliberately does not</h2>
 *
 * <pre>
 *   Direction          LONG / SHORT
 *   Historical odds    left-only, with its sample
 *   Market regime      the 50/200 trend state
 *   RSI divergence     when there is one
 * </pre>
 *
 * <p>The distance from the equator was here and is GONE (2026-10-17). A CTS point is an
 * independent timing event: it is not generated by a channel, it is not measured against one,
 * and its target and stop are multiples of volatility. Reporting a band-height distance beside
 * it suggested a relationship the study does not claim — and band heights and equator lines are
 * reserved for PTA and PTB, where they are the geometry the signal is MADE of.
 *
 * <p>`record.absDistance` is still computed by the odds engine and still arrives on the record.
 * That is the seam working rather than an oversight: the adapter decides what a family surfaces,
 * so the PTA and PTB adapters can show it without this one being touched.
 *
 * <p><b>One consequence, stated rather than hidden.</b> The odds figure above is still adjusted
 * by a distance cohort inside `ctsOddsEngine` — the four empirical rules it was specified with —
 * so the card no longer displays a quantity that is still one input to a number on it. Removing
 * that adjustment is a change to the ESTIMATOR and to the hover tooltip's own breakdown, which
 * is a separate decision from what this card displays.
 */
function ctsMetadata(record) {
  if (!record) return [];
  const rows = [
    { key: 'direction', label: 'Direction', value: record.direction === 'down' ? 'SHORT' : 'LONG' },
  ];
  rows.push({
    key: 'odds',
    label: 'Historical odds (left-only)',
    value: record.insufficient
      ? 'insufficient prior history'
      : `~${Math.round(record.odds * 100)}%`,
    // The sample is part of the figure, not a footnote. An odds number with no n is one a reader
    // can only accept or reject whole, which is the same reason the hover card carries a base
    // interval.
    note: `${record.sample} prior resolved setup${record.sample === 1 ? '' : 's'}`,
  });
  rows.push({
    key: 'regime',
    label: 'Market regime',
    value: record.regime && record.regime !== 'UNKNOWN'
      ? `${record.regime.charAt(0)}${record.regime.slice(1).toLowerCase()}`
      : 'not yet established',
    note: record.regime === 'UNKNOWN' ? 'fewer than 200 bars behind this one' : '50/200 SMA',
  });
  if (record.divergence) {
    rows.push({
      key: 'divergence',
      label: 'RSI divergence',
      value: record.divergence === 'BULLISH' ? 'Bullish' : 'Bearish',
      note: record.divergenceAgrees ? 'agrees with the signal' : 'CONFLICTS with the signal',
    });
  }
  return rows;
}


/* ── PTA AND PTB: the structural channels, where the geometry BELONGS ───────────────────── */

const fixed = (v, dp = 2) =>
  (typeof v === 'number' && Number.isFinite(v) ? v.toFixed(dp) : null);

/** `ZONE_1` -> `Zone 1`, and null stays null. */
const zoneLabel = (zone) =>
  (typeof zone === 'string' ? zone.replace('_', ' ').replace(/(\w)(\w*)/g,
    (_, a, b) => a + b.toLowerCase()) : null);

/**
 * THE CHANNEL CARD, and every row here is the thing CTS may not have.
 *
 * <p>A PTA or PTB element IS its geometry: the channel was measured from the bars of a
 * generation interval, and the top, the bottom, the equator between them and the excursion
 * ladder beyond are what the signal consists of. That is the whole reason the distance row was
 * stripped from CTS — there it described a channel the timing point has no relationship with,
 * and here it describes the element itself.
 *
 * <p>Where the OPEN already exists, the position rows are measured from the price the reader
 * actually filled at. Before that they describe the channel alone, because there is no position
 * to locate yet and inventing one from the last bar would put a reading on the card that no
 * decision of theirs produced.
 */
function channelMetadata(element) {
  if (!element) return [];
  const rows = [
    { key: 'channel', label: 'Channel', value: `${element.category} · ${element.date1}`,
      note: element.date2 ? `in force to ${element.date2}` : null },
    { key: 'top', label: 'Top (b0)', value: fixed(element.top) },
    { key: 'equator', label: 'Equator', value: fixed(element.equator),
      note: 'the midline between the measured edges' },
    { key: 'bottom', label: 'Bottom (b0)', value: fixed(element.bottom) },
    { key: 'height', label: 'Band height (h)', value: fixed(element.h),
      note: 'one excursion unit — every distance below is in these' },
  ];
  /*
   * `up` and `down` are ARRAYS — `[b0, b1, b2, b3]` out of `boundaryLevels`, not scalars.
   *
   * Found by opening the app: the first version tested `Number.isFinite(element.up)`, which is
   * false for an array, so the ladder and the zones silently never rendered on real data while
   * a fixture built from scalars passed every test. A fixture that does not match the shape the
   * code is handed proves nothing about it, which is why the suite now builds its sections
   * through `boundaryLevels` rather than by hand.
   */
  const up = Array.isArray(element.up) ? element.up : null;
  const down = Array.isArray(element.down) ? element.down : null;
  if (up && down && Number.isFinite(up[1]) && Number.isFinite(down[1])) {
    rows.push({
      key: 'ladder',
      label: 'Excursion ladder',
      value: `${fixed(down[1])} … ${fixed(up[1])}`,
      note: 'b1 either side — one band height beyond the channel',
    });
    /*
     * THE ZONE BOUNDS, which exist without a position and are structure rather than a reading.
     *
     * Added because "structural context (band heights, zones)" is asked for on an ARMED card,
     * and before an Open there is nothing to locate — a zone row that waited for a fill would
     * leave the card silent about the ladder a reader is deciding against. The `zone` row
     * below is the other question: not where the zones ARE, but which one the fill landed in.
     */
    rows.push({
      key: 'zones',
      label: 'Zones',
      value: `1: ${fixed(element.bottom)}–${fixed(element.top)}`,
      note: `2: out to ${fixed(down[1])} / ${fixed(up[1])} · 3: beyond`,
    });
  }
  if (element.position) {
    const p = element.position;
    rows.push({
      key: 'position',
      label: 'Open sits at',
      value: `${fixed(p.price)}  (${p.distance > 0 ? '+' : ''}${fixed(p.distance)}h)`,
      note: 'band heights from the equator, signed',
    });
    rows.push({
      key: 'zone',
      label: 'Structural zone',
      value: zoneLabel(p.zone) || 'outside the ladder',
      note: p.zone === 'ZONE_1' ? 'inside the channel'
        : p.zone === 'ZONE_2' ? 'the first extension, beyond b0'
          : p.zone === 'ZONE_3' ? 'past b1 — a long way outside' : null,
    });
  }
  return rows;
}

/**
 * The channel families, which differ in NOTHING but their label.
 *
 * <p>One factory rather than two literals: PTA and PTB are the same geometry on two cadences —
 * a long cycle and a short one — and the only thing a card needs to say differently is which it
 * is. Two hand-written adapters would be two places for the ladder's wording to drift.
 */
const channelProvider = (key, label) => Object.freeze({
  key,
  label,
  short: key,
  assesses: false,
  // NO RULES. The reader presses a direction and clicks the two legs; nothing here prices a
  // target or a stop, which is what `assesses: false` says and what the summary below repeats
  // in words a reader sees.
  rulesFor: () => null,
  metadata: channelMetadata,
  ruleSummary: () => 'You choose the direction, the entry and the exit. Both legs fill at the '
    + 'clicked BAR\'S CLOSE — not at the clicked price — so these trades and the CTS ones are '
    + 'measured the same way and the session ledger below can add them together. Structural '
    + 'guidance only: nothing here is advice, and no trade is taken for you.',
});

export const PROVIDERS = Object.freeze({
  CTS: Object.freeze({
    key: 'CTS',
    label: 'Critical Timing Signal',
    short: 'CTS',
    // The engine's assessment is what the card leads with, and the reader may still take their
    // own trade on the same point. The two land in one ledger and are told apart by `manual`
    // on the trade.
    assesses: true,
    rulesFor: () => CTS_RULES,
    metadata: ctsMetadata,
    /** Prose for the card, so the rule a result was measured under is never implicit. */
    ruleSummary: () => `Target and stop at ${CTS_RULES.targetAtr.toFixed(1)} ATR(14) either side `
      + `of the trigger close; closed at the horizon bar after ${CTS_RULES.horizon} bars if `
      + 'neither is reached. You can also take your own trade on this bar — lock a direction '
      + 'below and click the two legs; both land in the session ledger.',
  }),
  PTA: channelProvider('PTA', 'Long Cycle channel'),
  PTB: channelProvider('PTB', 'Short Cycle channel'),
});

/**
 * CHANNEL GEOMETRY — the vocabulary reserved for PTA and PTB, named so it can be asserted away.
 *
 * <p>A test sweeps every row a CTS card produces against this list. Named rather than left as an
 * absence because an absence cannot be tested for: "the distance row is gone" is true of the code
 * as written and says nothing about the code as it will be, whereas "no CTS row may mention a
 * band height" fails the day somebody reintroduces one under another label.
 *
 * <p><b>It is a CTS rule, not a global one.</b> The PTA and PTB cards are REQUIRED to use this
 * vocabulary — a channel IS its geometry — and a test asserts they do, against the same list.
 * One list read in both directions is what makes the separation checkable rather than asserted:
 * the terms that must never appear on one card are exactly the ones that must appear on the
 * other.
 */
export const CHANNEL_GEOMETRY_TERMS = Object.freeze([
  'equator', 'band height', 'band-height', 'heights', 'channel', 'midline',
  'zone', 'boundary', 'top line', 'bottom line',
]);

/** The adapter for {@code key}, or null. Null is a card that says so; CTS is never a fallback. */
export const providerFor = (key) => PROVIDERS[String(key || '').toUpperCase()] || null;

export default providerFor;
